import { useMutation } from '@apollo/client';
import { UpdateBee, UpdateBeeMutation, UpdateBeeMutationVariables } from '@serodigital/ies-api-client';
import {
  Icons,
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Radio,
  RadioGroup,
  RadioLabel,
  TableButton,
  Text,
  useModal,
  useToast,
} from '@serodigital/ui-components';
import * as React from 'react';
import { IDeviceInfo, ITrialDetail } from './state/types';

interface ITrialsProps {
  deviceInfo: IDeviceInfo;
  trials: ITrialDetail[];
  refetch: () => void;
}

export function Trials({ deviceInfo, trials, refetch }: ITrialsProps) {
  const {
    bee: { deviceId, trialId },
  } = deviceInfo;

  const { isOpen, toggle } = useModal();
  const { setToastMessage } = useToast();

  const [updateBee] = useMutation<UpdateBeeMutation, UpdateBeeMutationVariables>(UpdateBee);

  const [updatedTrialId, setUpdatedTrialId] = React.useState<string | null>(trialId);

  const [prevTrialId, setPrevTrialId] = React.useState<string>('');

  const submit = async () => {
    const trial = trials.find((item) => item.id === (updatedTrialId !== null ? updatedTrialId : prevTrialId));
    try {
      await updateBee({ variables: { bee: { deviceId, trialId: updatedTrialId } } });

      setToastMessage({
        label: `Successfully ${updatedTrialId !== null ? 'Added' : 'Removed'} Property ${
          updatedTrialId !== null ? 'To' : 'From'
        } "${trial.name}" Trial`,
        type: 'success',
        position: 'topCenter',
      });

      refetch();
      toggle();
    } catch {
      setToastMessage({
        label: `Failed to ${updatedTrialId !== null ? 'Add' : 'Remove'} Property ${
          updatedTrialId !== null ? 'To' : 'From'
        } "${trial.name}" Trial`,
        type: 'error',
        position: 'topCenter',
      });
    }
  };

  const handleRadioButtonChange = (trialId: string) => {
    setPrevTrialId(updatedTrialId);
    setUpdatedTrialId(trialId !== '' ? trialId : null);
  };

  return (
    <div className="flex justify-center">
      <TableButton data-qa={'trials-button'} icon={'left'} onClick={toggle}>
        <Icons.Plus />
        {!trials?.length ? 'Add' : 'Edit'} Trials
      </TableButton>
      <Modal
        data-qa="trials"
        isOpen={isOpen}
        onClose={() => {
          toggle();
        }}
      >
        <ModalTitle data-qa="trials-modal-title">Add / Remove Property Trials</ModalTitle>
        <ModalBody>
          <Text data-qa="trials-modal-description">Please select a trial from the list below:</Text>
          <RadioGroup onChange={(value) => handleRadioButtonChange(value)} className='py-24 flex flex-col'>
            <>
              <div className='flex flex-row pb-12'>
                <Radio id={'trials-checkbox-none'} data-qa={'remove-trial-checkbox'} checked={!trialId} value={''} />
                <RadioLabel htmlFor={'trials-checkbox-none'}>
                  <span>None</span>
                </RadioLabel>
              </div>
              {trials?.map((item, key) => (
                <div className='flex flex-row pb-12' key={key + item.name}>
                  <Radio
                    id={`trials-checkbox-equinox-${item.name}`}
                    data-qa={'trials-checkbox-equinox'}
                    checked={trialId === item.id}
                    value={item.id}
                  />
                  <RadioLabel htmlFor={`trials-checkbox-equinox-${item.name}`}>{item.name}</RadioLabel>
                </div>
              ))}
            </>
          </RadioGroup>
        </ModalBody>
        <ModalFooter
          onCancel={() => {
            toggle();
          }}
          onConfirm={submit}
          confirmLabel="Save"
        />
      </Modal>
    </div>
  );
}
